import type { JournalEntry } from "../types/index";
import React, { useState, useEffect } from "react"
import { Tabs, Tab, Box, Paper, Typography } from "@mui/material";
import JournalForm from "../components/JournalForm"
import JournalListing from "../components/JournalListing"
import {
  Snackbar,
  Alert,
} from "@mui/material"
import { ThemeProvider, keyframes } from "@mui/material/styles"
import CssBaseline from "@mui/material/CssBaseline"
import { useNavigate } from "react-router-dom"
import { useTheme } from "@mui/material/styles"
import { AddJournal, GetJournals, DeleteJournal, GetTeamJournals } from "../utils/APIRequest"

// Smooth animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`

// Interfaces
interface FormErrors {
  feedbackType?: string
  feedbackCategory?: string
  feedbackText?: string
  [key: string]: string | undefined
}



const getUserDesignation = () => {
  return JSON.parse(localStorage.getItem('user') || '{}').designation || 'employee';
};

const EmployeeJournal = () => {
  const [currentView, setCurrentView] = useState<"list" | "form">("list")
  const [tab, setTab] = useState<'myJournal' | 'teamJournals'>('myJournal');
  const userDesignation = getUserDesignation();
  const [loading, setLoading] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)

  // Form states
  const [feedbackType, setFeedbackType] = useState("")
  const [feedbackCategory, setFeedbackCategory] = useState("")
  const [feedbackDate, setFeedbackDate] = useState(new Date().toISOString().split("T")[0])
  const [feedbackText, setFeedbackText] = useState("")
  const [showSuccess, setShowSuccess] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})
  // State for team journals (for Team Journals tab)
  const [teamJournalEntries, setTeamJournalEntries] = useState<JournalEntry[]>([]);

  const navigate = useNavigate()
  const theme = useTheme();

  // List states
  const [viewFilter, setViewFilter] = useState("This Year")
  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>([])

  const characterCount = feedbackText.length
  const minCharacters = 200
  const progressValue = Math.min((characterCount / minCharacters) * 100, 100)

  useEffect(() => {
    setIsLoaded(true);
    const fetchJournals = async () => {
      const journals = await GetJournals();
      // Map _id to id and ensure createdAt exists for compatibility
      const mappedJournals = journals.map((j: any) => ({
        ...j,
        id: (j.id || j._id)?.toString(),
        createdAt: j.createdAt || j.dateOfJournal || new Date().toISOString(),
      }));
      setJournalEntries(mappedJournals);
    };
    fetchJournals();
  }, []);

  const validateForm = () => {
    const newErrors: FormErrors = {}
    if (!feedbackType) newErrors.feedbackType = "Please select feedback type"
    if (!feedbackCategory) newErrors.feedbackCategory = "Please select feedback category"
    if (characterCount < minCharacters) {
      newErrors.feedbackText = `Feedback must be at least ${minCharacters} characters`
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = async (formData: FormData) => {
    if (validateForm()) {
      setLoading(true);
      try {
        const response = await AddJournal(formData);
        if (response?.status === "success") {
          window.location.reload();
        } else {
          // Optionally show error
        }
      } catch (error) {
        // Optionally show error
      } finally {
        setLoading(false);
      }
    }
  }

  const handleAddJournal = () => {
    setFeedbackType("");
    setFeedbackCategory("");
    setFeedbackDate(new Date().toISOString().split("T")[0]);
    setFeedbackText("");
    setErrors({});
    setCurrentView("form");
  }

  const handleBackToList = () => {
    setCurrentView("list");
    setFeedbackType("");
    setFeedbackCategory("");
    setFeedbackText("");
    setErrors({});
  }

  const filteredEntries = journalEntries.filter((entry) => {
    const entryDate = new Date(entry.createdAt);
    const now = new Date();
    if (viewFilter === "Last 3 months") {
      const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
      return entryDate >= threeMonthsAgo;
    } else if (viewFilter === "Last 6 months") {
      const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
      return entryDate >= sixMonthsAgo;
    } else {
      // Default: This Year
      return entryDate.getFullYear() === now.getFullYear();
    }
  });

  const getTeamLeadInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
  }

  // Delete handler
  const handleDeleteJournal = async (journalId: string) => {
    setLoading(true);
    try {
      await DeleteJournal(journalId);
      const journals = await GetJournals();
      // Map _id to id if necessary
      const mappedJournals = journals.map((j: any) => ({
        ...j,
        id: (j.id || j._id)?.toString()
      }));
      setJournalEntries(mappedJournals);
    } catch (error) {
      // Optionally show error
    } finally {
      setLoading(false);
    }
  };

  // Fetch team journals for Team Journals tab
  useEffect(() => {
    if (userDesignation === 'team_lead' || userDesignation === 'asso_team_lead') {
      const fetchTeamJournals = async () => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const teamJournals = await GetTeamJournals(user.name);
        // Map _id to id and ensure createdAt exists for compatibility
        const mappedJournals = teamJournals.map((j: any) => ({
          ...j,
          id: (j.id || j._id)?.toString(),
          createdAt: j.createdAt || j.dateOfJournal || new Date().toISOString(),
          employeeName: j.user.emp_name || '',
        }));
        setTeamJournalEntries(mappedJournals);
      };
      fetchTeamJournals();
    }
  }, [userDesignation]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {userDesignation === "team_lead" || userDesignation === "asso_team_lead" ? (
        <Paper elevation={3}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <Tab
              value="myJournal"
              label="My Journal"
              sx={{ textTransform: "none", fontSize: "1rem" }}
            />
            <Tab
              value="teamJournals"
              label="Team Journals"
              sx={{ textTransform: "none", fontSize: "1rem" }}
            />
          </Tabs>
          <Box hidden={tab !== 'myJournal'}>
            {isLoaded && (currentView === "list" ? (
              <JournalListing
                theme={theme}
                navigate={navigate}
                viewFilter={viewFilter}
                setViewFilter={setViewFilter}
                filteredEntries={filteredEntries}
                handleAddJournal={handleAddJournal}
                handleEditJournal={() => { }}
                getTeamLeadInitials={getTeamLeadInitials}
                fadeInUp={fadeInUp}
                slideInLeft={slideInLeft}
                deleteJournal={handleDeleteJournal}
              />
            ) : (
              <JournalForm
                theme={theme}
                navigate={navigate}
                feedbackType={feedbackType}
                setFeedbackType={(val) => {
                  setFeedbackType(val)
                  setErrors((prev) => ({ ...prev, feedbackType: undefined }))
                }}
                feedbackCategory={feedbackCategory}
                setFeedbackCategory={(val) => {
                  setFeedbackCategory(val)
                  setErrors((prev) => ({ ...prev, feedbackCategory: undefined }))
                }}
                feedbackDate={feedbackDate}
                setFeedbackDate={setFeedbackDate}
                feedbackText={feedbackText}
                setFeedbackText={(val) => {
                  setFeedbackText(val)
                  setErrors((prev) => ({ ...prev, feedbackText: undefined }))
                }}
                errors={errors}
                loading={loading}
                handleBackToList={handleBackToList}
                handleSave={handleSave}
                characterCount={characterCount}
                minCharacters={minCharacters}
                progressValue={progressValue}
                isEdit={false}
                existingFiles={[]}
              />
            ))}
          </Box>
          <Box hidden={tab !== 'teamJournals'}>
            <JournalListing
              theme={theme}
              navigate={navigate}
              viewFilter={viewFilter}
              setViewFilter={setViewFilter}
              filteredEntries={teamJournalEntries}
              handleAddJournal={() => { }}
              handleEditJournal={() => { }}
              getTeamLeadInitials={getTeamLeadInitials}
              fadeInUp={fadeInUp}
              slideInLeft={slideInLeft}
              deleteJournal={() => { }}
              isTeamJournal={true}
            />
          </Box>
        </Paper>
      ) : (
        isLoaded && (currentView === "list" ? (
          <JournalListing
            theme={theme}
            navigate={navigate}
            viewFilter={viewFilter}
            setViewFilter={setViewFilter}
            filteredEntries={filteredEntries}
            handleAddJournal={handleAddJournal}
            handleEditJournal={() => { }}
            getTeamLeadInitials={getTeamLeadInitials}
            fadeInUp={fadeInUp}
            slideInLeft={slideInLeft}
            deleteJournal={handleDeleteJournal}
          />
        ) : (
          <JournalForm
            theme={theme}
            navigate={navigate}
            feedbackType={feedbackType}
            setFeedbackType={(val) => {
              setFeedbackType(val)
              setErrors((prev) => ({ ...prev, feedbackType: undefined }))
            }}
            feedbackCategory={feedbackCategory}
            setFeedbackCategory={(val) => {
              setFeedbackCategory(val)
              setErrors((prev) => ({ ...prev, feedbackCategory: undefined }))
            }}
            feedbackDate={feedbackDate}
            setFeedbackDate={setFeedbackDate}
            feedbackText={feedbackText}
            setFeedbackText={(val) => {
              setFeedbackText(val)
              setErrors((prev) => ({ ...prev, feedbackText: undefined }))
            }}
            errors={errors}
            loading={loading}
            handleBackToList={handleBackToList}
            handleSave={handleSave}
            characterCount={characterCount}
            minCharacters={minCharacters}
            progressValue={progressValue}
            isEdit={false}
            existingFiles={[]}
          />
        ))
      )}
      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success"
          variant="filled"
          sx={{
            borderRadius: 2,
            boxShadow: `0 8px 32px ${theme.palette.primary.main}`,
          }}
        >
          Feedback saved successfully! Thank you for your valuable input.
        </Alert>
      </Snackbar>
    </ThemeProvider>
  )
}

export default EmployeeJournal